import Grid from '@material-ui/core/Grid';
import React, {useCallback, useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import styled from 'styled-components';
import {
    Accordion,
    AccordionItemProps,
    AccordionProps,
    GridContainer,
    Hyperlink,
    Typography,
    VerticalSpacer
} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps';
import {SearchField} from '../../elements/inputs/search/SearchField';
import {useBreakpointDown} from 'epic-ue-theme';

const checkQuestionMatch = (
    filteredList: AccordionItemProps[],
    filterLower: string,
    item: AccordionItemProps
) => {
    const {title = '', description = ''} = item;
    if (
        title.toLowerCase().indexOf(filterLower) > -1 ||
        description.toLowerCase().indexOf(filterLower) > -1
    ) {
        filteredList.push(item);
    }
};

const getFilteredQuestions = (questionList: AccordionItemProps[], filterStr = '') => {
    if (!filterStr) {
        return questionList;
    }

    const filterLower = filterStr.toLowerCase();
    const filteredList: AccordionItemProps[] = [];

    questionList.forEach((item) => {
        const itemLength = Object.keys(item).length;
        if (item && itemLength) {
            checkQuestionMatch(filteredList, filterLower, item);
        }
    });

    return filteredList;
};

const Styled = styled.div`
    .search-field .MuiInputBase-root {
        background: white;
        font-family: 'Graphik LCG Web';
    }
    .ue-accordion-item div:only-child {
        min-height: unset;
    }
`;
const TitlesSidebar = styled.div`
    .hlink-wrap {
        color: ${(p) => p.theme.palette.text.secondary};
        opacity: 0.5;
        transition: opacity 0.25s ${(p) => p.theme.easing};
        &.active,
        &:hover {
            opacity: 1;
        }
    }
    .heading.h5 {
        text-transform: initial;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        &.titles-collapsed,
        &.titles-expanded {
            position: fixed;
            top: 0px;
            left: 0px;
            background-color: ${(p) =>
                p.theme.ifLight(
                    p,
                    p.theme.palette.neutral.pearl,
                    p.theme.palette.neutral.concrete
                )};
            z-index: 3;
            box-sizing: border-box;
            width: 100%;
            text-align: center;
            padding-top: ${(p) => p.theme.spacing(2)}px;
            padding-bottom: ${(p) => p.theme.spacing(2)}px;
        }
    }

    a.ue-link-wrap {
        white-space: initial;
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)};
        &:hover {
            color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
        }
    }
`;

export interface FAQSearchProps extends AccordionProps {
    resultsTitle?: string;
    noResultsDescription?: string;
    noResultsTitle?: string;
    noResultsSuggestions?: AccordionItemProps[];
    searchLabelText?: string;
    initialSectionTitle?: string;
    disableSearchField?: boolean;
}

export const FAQSearch = ({
    items = [],
    openAllText,
    resultsTitle = 'Search results',
    noResultsDescription,
    noResultsTitle,
    noResultsSuggestions,
    searchLabelText,
    accentColor,
    initialSectionTitle = '',
    disableSearchField
}: FAQSearchProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const [titlesSidebarCollapsed, setTitlesSidebarCollapsed] = useState(true);
    const toggleTitlesSidebarCollapse = (e) => {
        e.preventDefault();
        setTitlesSidebarCollapsed(!titlesSidebarCollapsed);
    };
    const {ref, inView} = useInView();

    const firstSection = items?.[0] || {};
    const [searchText, setSearchText] = useState('');
    const filteredView = Boolean(searchText && searchText.length);
    const [activeSection, setActiveSection] = useState<AccordionItemProps>(firstSection);
    const clickSectionTab = useCallback(
        (section) => {
            if (!section) return;
            setActiveSection(section);
        },
        [setActiveSection]
    );

    const getSectionItemFromTitle = (sectionTitle) => {
        const lowerSectionTitle = sectionTitle.replace(/-/g, ' ').toLowerCase();
        const foundSection = items?.find(
            (item, i) => item?.title?.toLowerCase() === lowerSectionTitle
        );
        return foundSection;
    };

    useEffect(() => {
        if (initialSectionTitle) {
            const initalSection = getSectionItemFromTitle(initialSectionTitle);
            initalSection && setActiveSection(initalSection);
        }
    }, [initialSectionTitle]);

    if (!items?.length) {
        return null;
    }

    let noResults = false;
    let masterQuestionList: AccordionItemProps[] = [];
    let filteredQuestionList: AccordionItemProps[] = activeSection?.items || [];
    if (filteredView) {
        items?.forEach((item) => {
            const questionList = item?.items || [];
            masterQuestionList = masterQuestionList.concat(questionList);
        });

        filteredQuestionList = getFilteredQuestions(masterQuestionList, searchText);
        noResults = !filteredQuestionList || filteredQuestionList.length === 0;
    }

    return (
        <AnimateOnViewWrap translateY={30}>
            <Styled>
                <GridContainer>
                    <Grid container spacing={2}>
                        {!disableSearchField && (
                            <Grid ref={ref} item xs={12}>
                                <SearchField
                                    searchLabelText={searchLabelText}
                                    variant="large"
                                    accentColor={accentColor}
                                    onChange={(searchText) => {
                                        setSearchText(searchText);
                                    }}
                                />
                                <VerticalSpacer variant="custom" spacing={7} downSmSpacing={2} />
                            </Grid>
                        )}
                        <Grid item xs={12} md={4}>
                            {isSm && !inView ? (
                                titlesSidebarCollapsed ? (
                                    <TitlesSidebar className="titles-collapsed">
                                        <Typography variant="h5">
                                            <Hyperlink
                                                accentColor={accentColor}
                                                href={'/faq'}
                                                component="a"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    toggleTitlesSidebarCollapse(e);
                                                }}>
                                                {activeSection.title}
                                            </Hyperlink>
                                        </Typography>
                                    </TitlesSidebar>
                                ) : (
                                    <TitlesSidebar className="titles-expanded">
                                        {items?.map((item, i) => (
                                            <React.Fragment key={`faq-search-section-xs-${i}`}>
                                                <Typography variant="h5">
                                                    <Hyperlink
                                                        accentColor={accentColor}
                                                        active={
                                                            filteredView
                                                                ? false
                                                                : item.title === activeSection.title
                                                        }
                                                        key={i}
                                                        href={'/faq'}
                                                        component="a"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            toggleTitlesSidebarCollapse(e);
                                                            clickSectionTab(item);
                                                        }}>
                                                        {item.title}
                                                    </Hyperlink>
                                                </Typography>
                                                {i !== items?.length - 1 ? (
                                                    <VerticalSpacer variant="eyebrow" />
                                                ) : (
                                                    ''
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TitlesSidebar>
                                )
                            ) : (
                                <TitlesSidebar>
                                    <VerticalSpacer variant="custom" spacing={1} />
                                    {items?.map((item, i) => (
                                        <React.Fragment key={`faq-search-section-${i}`}>
                                            <Typography variant="h5">
                                                <Hyperlink
                                                    accentColor={accentColor}
                                                    active={
                                                        filteredView
                                                            ? false
                                                            : item.title === activeSection.title
                                                    }
                                                    key={i}
                                                    href={'/faq'}
                                                    component="a"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        clickSectionTab(item);
                                                    }}>
                                                    {item.title}
                                                </Hyperlink>
                                            </Typography>
                                            {i !== items?.length - 1 ? (
                                                <VerticalSpacer variant="eyebrow" />
                                            ) : (
                                                ''
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TitlesSidebar>
                            )}
                            <VerticalSpacer variant="custom" downSmSpacing={5} />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {noResults ? (
                                <>
                                    <Typography variant="p">
                                        {noResultsDescription || 'No results'}
                                    </Typography>
                                    <VerticalSpacer variant="custom" spacing={4} />
                                    <Accordion
                                        titleAboveItems={true}
                                        openAllText={openAllText}
                                        accentColor={accentColor}
                                        items={[
                                            {
                                                title: noResultsTitle,
                                                items: noResultsSuggestions
                                            }
                                        ]}
                                    />
                                </>
                            ) : (
                                <React.Fragment
                                    key={`faq-search-section-xs-${activeSection?.title}`}>
                                    <Accordion
                                        titleAboveItems={true}
                                        openAllText={openAllText}
                                        accentColor={accentColor}
                                        items={[
                                            {
                                                title: filteredView
                                                    ? resultsTitle
                                                    : activeSection?.title,
                                                items: filteredQuestionList
                                            }
                                        ]}
                                    />
                                </React.Fragment>
                            )}
                        </Grid>
                    </Grid>
                </GridContainer>
            </Styled>
        </AnimateOnViewWrap>
    );
};
